import { translationBaseKey } from '../../../../../decorators/translation-base-key.decorator';
import {
  advancedFormGroup,
  advancedFormControl,
  FormControlTypeEnum,
} from '@magieno/common';
import { IsNotEmpty, IsNumber, Max, Min, validateIf, ValidateNested } from '@pristine-ts/class-validator';
import { FieldTypeEnum } from '../../../../../enums/field-type.enum';
import { ErrorMessageBuilder } from '../../../../../builders/error-message.builder';
import {
  CorporateIncomeTaxQuebecInSalaryDividendOptions,
} from './corporate-income-tax-quebec-in-salary-dividend.options';
import {
  CorporateIncomeTaxFederalInSalaryDividendOptions,
} from './corporate-income-tax-federal-in-salary-dividend.options';
import { CorporateIncomeTaxOptions } from '../../../income-tax/corporate/corporate/corporate-income-tax.options';
import { CompanyTypeEnum } from '../../../../../enums/company-type.enum';
import {
  CorporateIncomeTaxConnectedCorporationOptions,
} from '../../../income-tax/corporate/common/corporate-income-tax-connected-corporation.options';
import { CalculationDate } from '../../../../../models/calculation.date';
import { clonePropertyMetadata } from '@pristine-ts/metadata';
import {
  CorporateIncomeTaxCommonOptions,
} from '../../../income-tax/corporate/common/corporate-income-tax-common.options';

@translationBaseKey('income-tax-corporation-in-salary-dividend-options')
export class CorporateIncomeTaxInSalaryDividendOptions implements Omit<CorporateIncomeTaxOptions,
  'calculationKeyname' |
  'fiscalYear' |
  'donations' |
  'incomeTaxCorporationFederal' |
  'incomeTaxCorporationQuebec' |
  'taxableGainLossExcedent'
> {

  @advancedFormGroup({
    classType: CorporateIncomeTaxQuebecInSalaryDividendOptions,
  })
  @ValidateNested()
  incomeTaxCorporationQuebec: CorporateIncomeTaxQuebecInSalaryDividendOptions;

  @advancedFormGroup({
    classType: CorporateIncomeTaxFederalInSalaryDividendOptions,
  })
  @ValidateNested()
  incomeTaxCorporationFederal: CorporateIncomeTaxFederalInSalaryDividendOptions;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Dans le cas ou le salaire simulé engendre une perte pour la société, quel est le taux d\'imposition de la société quant au report de cette perte?',
        en: 'If the simulated salary causes a loss for the corporation, what it the tax rate for the loss.',
      },
      tooltip: {
        fr: 'Le logiciel a besoin du taux d’imposition lors d’un report de perte lorsque le salaire simulé par le logiciel plus les cotisations de l’employeur équivalente engendre une perte dans l’exercice qui est reporté à un autre exercice. Le logiciel pourra calculer l’économie d’impôt relatif au report de perte.',
        en: 'The software needs the tax rate when carrying forward a loss when the salary simulated by the software plus the equivalent employer contributions results in a loss in the fiscal year that is carried forward to another fiscal year. The software will be able to calculate the tax savings relating to loss carryover.',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Input,
  })
  @IsNumber(undefined, { buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @Min(0, { buildErrorMessage: ErrorMessageBuilder.build })
  @Max(100, { buildErrorMessage: ErrorMessageBuilder.build })
  lossTaxRate: number;  // todo used only for salary dividend ?

  @clonePropertyMetadata<CorporateIncomeTaxCommonOptions>(CorporateIncomeTaxCommonOptions, 'adjustedInvestmentIncome')
  adjustedInvestmentIncome: number;

  @clonePropertyMetadata<CorporateIncomeTaxCommonOptions>(CorporateIncomeTaxCommonOptions, 'businessIncome')
  businessIncome: number;

  @clonePropertyMetadata<CorporateIncomeTaxCommonOptions>(CorporateIncomeTaxCommonOptions, 'capitalLossBalance')
  capitalLossBalance: number;

  @clonePropertyMetadata<CorporateIncomeTaxCommonOptions>(CorporateIncomeTaxCommonOptions, 'companyType')
  companyType: CompanyTypeEnum;

  @clonePropertyMetadata<CorporateIncomeTaxCommonOptions>(CorporateIncomeTaxCommonOptions, 'connectedCorporations')
  connectedCorporations: CorporateIncomeTaxConnectedCorporationOptions[] = [];

  @clonePropertyMetadata<CorporateIncomeTaxCommonOptions>(CorporateIncomeTaxCommonOptions, 'fiscalYearEnd')
  fiscalYearEnd: CalculationDate;

  @clonePropertyMetadata<CorporateIncomeTaxCommonOptions>(CorporateIncomeTaxCommonOptions, 'fiscalYearStart')
  fiscalYearStart: CalculationDate;

  @clonePropertyMetadata<CorporateIncomeTaxCommonOptions>(CorporateIncomeTaxCommonOptions, 'nonCapitalLossBalance')
  nonCapitalLossBalance: number;

  @clonePropertyMetadata<CorporateIncomeTaxCommonOptions>(CorporateIncomeTaxCommonOptions, 'totalTaxableCapital')
  totalTaxableCapital: number;

  @clonePropertyMetadata<CorporateIncomeTaxCommonOptions>(CorporateIncomeTaxCommonOptions, 'assignedBusinessLimit')
  @validateIf((object: CorporateIncomeTaxCommonOptions, root, currentPath) => object.companyType === CompanyTypeEnum.ccpcAllYear)
  assignedBusinessLimit?: number;

  @clonePropertyMetadata<CorporateIncomeTaxOptions>(CorporateIncomeTaxOptions, 'interestIncome')
  interestIncome: number;
}
